import { useState, useEffect, useCallback, useRef } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useMessage } from '../hooks/useMessage'
import { CurrencySelect } from '../components/CurrencySelect'
import { TIMEOUTS } from '../constants'
import './Settings.css'

interface SettingsData {
  fresh_water_tank_capacity: number
  grey_water_tank_capacity: number
  daily_base_water_usage: number
  avg_shower_usage: number
  native_currency: string
  battery_charge_avg_window_minutes: number
  battery_db_write_interval_minutes: number
  show_idle_chargers: boolean
  webhook_url: string
  webhook_header_name: string
  webhook_header_value_masked: string
  webhook_depart_enabled: boolean
  webhook_arrive_enabled: boolean
}

interface ScopedAPIKey {
  id: number
  name: string
  prefix: string
  scope: string
  created_at: string
  revoked_at?: string
}

interface FormData {
  fresh_water_tank_capacity: string
  grey_water_tank_capacity: string
  daily_base_water_usage: string
  avg_shower_usage: string
  native_currency: string
  battery_charge_avg_window_minutes: string
  battery_db_write_interval_minutes: string
  show_idle_chargers: boolean
}

export function Settings() {
  const { user } = useAuth()
  const { message, showError, showSuccess } = useMessage()
  const [formData, setFormData] = useState<FormData>({
    fresh_water_tank_capacity: '',
    grey_water_tank_capacity: '',
    daily_base_water_usage: '',
    avg_shower_usage: '',
    native_currency: '',
    battery_charge_avg_window_minutes: '',
    battery_db_write_interval_minutes: '',
    show_idle_chargers: false,
  })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [apiKeyLoaded, setApiKeyLoaded] = useState(false)
  const [apiKeyConfigured, setApiKeyConfigured] = useState(false)
  const [newApiKey, setNewApiKey] = useState('')
  const [showNewKeyInput, setShowNewKeyInput] = useState(false)
  const [justSavedKey, setJustSavedKey] = useState<string | null>(null)

  // Outgoing webhook state
  const [webhookUrl, setWebhookUrl] = useState('')
  const [webhookHeaderName, setWebhookHeaderName] = useState('')
  const [webhookHeaderValue, setWebhookHeaderValue] = useState('')
  const [webhookHeaderValueMasked, setWebhookHeaderValueMasked] = useState('')
  const [webhookDepartEnabled, setWebhookDepartEnabled] = useState(true)
  const [webhookArriveEnabled, setWebhookArriveEnabled] = useState(true)
  const [savingWebhook, setSavingWebhook] = useState(false)
  const [testingWebhook, setTestingWebhook] = useState(false)

  // Scoped read-only API keys state
  const [scopedKeys, setScopedKeys] = useState<ScopedAPIKey[]>([])
  const [scopedKeysLoaded, setScopedKeysLoaded] = useState(false)
  const [newKeyName, setNewKeyName] = useState('')
  const [createdScopedKey, setCreatedScopedKey] = useState<string | null>(null)
  const [creatingScopedKey, setCreatingScopedKey] = useState(false)

  const isAdmin = user?.role === 'admin'

  // Backup/restore state
  const [showRestoreDialog, setShowRestoreDialog] = useState(false)
  const [restoreFile, setRestoreFile] = useState<File | null>(null)
  const [restoreConfirmText, setRestoreConfirmText] = useState('')
  const [restoreUnderstood, setRestoreUnderstood] = useState(false)
  const [restoring, setRestoring] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const fetchSettings = useCallback(async () => {
    try {
      const response = await fetch('/api/v1/settings')
      if (!response.ok) {
        throw new Error('Failed to fetch settings')
      }
      const data: SettingsData = await response.json()
      setFormData({
        fresh_water_tank_capacity: data.fresh_water_tank_capacity.toString(),
        grey_water_tank_capacity: data.grey_water_tank_capacity.toString(),
        daily_base_water_usage: data.daily_base_water_usage.toString(),
        avg_shower_usage: data.avg_shower_usage.toString(),
        native_currency: data.native_currency,
        battery_charge_avg_window_minutes: data.battery_charge_avg_window_minutes.toString(),
        battery_db_write_interval_minutes: data.battery_db_write_interval_minutes.toString(),
        show_idle_chargers: data.show_idle_chargers,
      })
      setWebhookUrl(data.webhook_url || '')
      setWebhookHeaderName(data.webhook_header_name || '')
      setWebhookHeaderValueMasked(data.webhook_header_value_masked || '')
      // The server defaults both toggles to true, but guard against older
      // responses that omit the fields.
      setWebhookDepartEnabled(data.webhook_depart_enabled !== false)
      setWebhookArriveEnabled(data.webhook_arrive_enabled !== false)
    } catch {
      showError('Failed to load settings')
    } finally {
      setLoading(false)
    }
  }, [showError])

  const fetchScopedKeys = useCallback(async () => {
    try {
      const response = await fetch('/api/v1/api-keys')
      if (!response.ok) {
        // Guests cannot list keys; the section stays hidden.
        return
      }
      const data: { keys?: ScopedAPIKey[] } = await response.json()
      setScopedKeys(data.keys || [])
      setScopedKeysLoaded(true)
    } catch {
      // Ignore errors; the section just shows nothing
    }
  }, [])

  const fetchApiKey = useCallback(async () => {
    try {
      const response = await fetch('/api/v1/settings/api-key')
      if (response.ok) {
        const data = await response.json()
        setApiKeyConfigured(data.configured === true)
        setApiKeyLoaded(true)
      }
      // If 403, user is not admin - that's fine, just don't show key
    } catch {
      // Ignore errors - API key section just won't work for non-admins
    }
  }, [])

  useEffect(() => {
    if (!user) return
    fetchSettings()
    fetchApiKey()
    fetchScopedKeys()
  }, [user, fetchSettings, fetchApiKey, fetchScopedKeys])

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const validateForm = (): string | null => {
    const freshWater = parseFloat(formData.fresh_water_tank_capacity)
    const greyWater = parseFloat(formData.grey_water_tank_capacity)
    const dailyUsage = parseFloat(formData.daily_base_water_usage)
    const showerUsage = parseFloat(formData.avg_shower_usage)
    const chargeWindow = parseInt(formData.battery_charge_avg_window_minutes)
    const dbWriteInterval = parseInt(formData.battery_db_write_interval_minutes)

    if (isNaN(freshWater) || freshWater <= 0) {
      return 'Fresh water tank capacity must be greater than 0'
    }
    if (isNaN(greyWater) || greyWater <= 0) {
      return 'Grey water tank capacity must be greater than 0'
    }
    if (isNaN(dailyUsage) || dailyUsage < 0) {
      return 'Daily base water usage must be 0 or greater'
    }
    if (isNaN(showerUsage) || showerUsage < 0) {
      return 'Average shower usage must be 0 or greater'
    }
    if (!formData.native_currency.trim()) {
      return 'Native currency is required'
    }
    if (isNaN(chargeWindow) || chargeWindow < 1 || chargeWindow > 600) {
      return 'Charge averaging window must be 1-600 minutes'
    }
    if (isNaN(dbWriteInterval) || dbWriteInterval < 1 || dbWriteInterval > 30) {
      return 'DB write interval must be 1-30 minutes'
    }
    return null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const error = validateForm()
    if (error) {
      showError(error)
      return
    }

    setSaving(true)

    try {
      const response = await fetch('/api/v1/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fresh_water_tank_capacity: parseFloat(formData.fresh_water_tank_capacity),
          grey_water_tank_capacity: parseFloat(formData.grey_water_tank_capacity),
          daily_base_water_usage: parseFloat(formData.daily_base_water_usage),
          avg_shower_usage: parseFloat(formData.avg_shower_usage),
          native_currency: formData.native_currency.trim(),
          battery_charge_avg_window_minutes: parseInt(formData.battery_charge_avg_window_minutes),
          battery_db_write_interval_minutes: parseInt(formData.battery_db_write_interval_minutes),
          show_idle_chargers: formData.show_idle_chargers,
        }),
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || 'Failed to save settings')
      }

      await response.json()
      showSuccess('Settings saved successfully')
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to save settings')
    } finally {
      setSaving(false)
    }
  }

  const copyApiKey = (key: string) => {
    navigator.clipboard.writeText(key)
    showSuccess('API key copied to clipboard', TIMEOUTS.MESSAGE_DISMISS_SHORT)
  }

  const generateRandomKey = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
    let key = ''
    const array = new Uint8Array(32)
    crypto.getRandomValues(array)
    for (const byte of array) key += chars[byte % chars.length]
    setNewApiKey(key)
  }

  const saveNewApiKey = async () => {
    if (newApiKey.length < 16) {
      showError('API key must be at least 16 characters')
      return
    }
    try {
      const response = await fetch('/api/v1/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ api_key: newApiKey }),
      })
      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || 'Failed to save API key')
      }
      setJustSavedKey(newApiKey)
      setShowNewKeyInput(false)
      setNewApiKey('')
      setApiKeyConfigured(true)
      showSuccess('API key saved. Copy it now — it cannot be shown again.')
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to save API key')
    }
  }

  const saveWebhook = async () => {
    setSavingWebhook(true)
    try {
      // The header value is write-only: blank means "keep the stored value".
      const payload: Record<string, string | boolean> = {
        webhook_url: webhookUrl.trim(),
        webhook_header_name: webhookHeaderName.trim(),
        webhook_depart_enabled: webhookDepartEnabled,
        webhook_arrive_enabled: webhookArriveEnabled,
      }
      if (webhookHeaderValue.trim() !== '') {
        payload.webhook_header_value = webhookHeaderValue
      }

      const response = await fetch('/api/v1/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      const data: SettingsData = await response.json()
      if (!response.ok) {
        throw new Error((data as unknown as { error?: string }).error || 'Failed to save webhook settings')
      }
      setWebhookHeaderValueMasked(data.webhook_header_value_masked || '')
      setWebhookHeaderValue('')
      showSuccess('Webhook settings saved')
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to save webhook settings')
    } finally {
      setSavingWebhook(false)
    }
  }

  const clearWebhookHeaderValue = async () => {
    if (!confirm('Clear the stored webhook header value?')) return
    try {
      const response = await fetch('/api/v1/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ webhook_header_value: '' }),
      })
      const data: SettingsData = await response.json()
      if (!response.ok) {
        throw new Error((data as unknown as { error?: string }).error || 'Failed to clear webhook header value')
      }
      setWebhookHeaderValueMasked(data.webhook_header_value_masked || '')
      showSuccess('Webhook header value cleared')
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to clear webhook header value')
    }
  }

  const testWebhook = async () => {
    setTestingWebhook(true)
    try {
      const response = await fetch('/api/v1/settings/webhook/test', { method: 'POST' })
      const data = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(data.error || 'Test webhook failed')
      }
      showSuccess(data.message || 'Test webhook delivered')
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Test webhook failed')
    } finally {
      setTestingWebhook(false)
    }
  }

  const createScopedKey = async () => {
    if (!newKeyName.trim()) {
      showError('Enter a name for the key')
      return
    }
    setCreatingScopedKey(true)
    try {
      const response = await fetch('/api/v1/api-keys', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newKeyName.trim() }),
      })
      const data = await response.json()
      if (!response.ok) {
        throw new Error(data.error || 'Failed to create API key')
      }
      setCreatedScopedKey(data.key)
      setNewKeyName('')
      showSuccess('API key created. Copy it now — it cannot be shown again.')
      fetchScopedKeys()
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to create API key')
    } finally {
      setCreatingScopedKey(false)
    }
  }

  const revokeScopedKey = async (id: number, name: string) => {
    if (!confirm(`Revoke API key "${name}"? Devices using it will stop working.`)) return
    try {
      const response = await fetch(`/api/v1/api-keys/${id}`, { method: 'DELETE' })
      const data = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(data.error || 'Failed to revoke API key')
      }
      showSuccess('API key revoked')
      fetchScopedKeys()
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to revoke API key')
    }
  }

  const handleBackupDownload = () => {
    window.location.href = '/api/v1/backup'
  }

  const handleRestoreFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      if (!file.name.toLowerCase().endsWith('.zip')) {
        showError('Please select a ZIP backup file')
        return
      }
      setRestoreFile(file)
      setShowRestoreDialog(true)
      setRestoreConfirmText('')
      setRestoreUnderstood(false)
    }
  }

  const handleRestoreCancel = () => {
    setShowRestoreDialog(false)
    setRestoreFile(null)
    setRestoreConfirmText('')
    setRestoreUnderstood(false)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleRestoreConfirm = async () => {
    if (!restoreFile || restoreConfirmText !== 'RESTORE' || !restoreUnderstood) {
      return
    }

    setRestoring(true)

    try {
      const formData = new FormData()
      formData.append('file', restoreFile)
      formData.append('confirm', 'RESTORE')

      const response = await fetch('/api/v1/restore', {
        method: 'POST',
        body: formData,
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Restore failed')
      }

      showSuccess(data.message || 'Restore successful! Reloading...', 0) // Don't auto-dismiss
      handleRestoreCancel()

      // Reload page after a short delay
      setTimeout(() => {
        window.location.reload()
      }, 2000)
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Restore failed')
    } finally {
      setRestoring(false)
    }
  }

  if (loading) {
    return (
      <div className="page settings">
        <h1>Settings</h1>
        <p>Loading settings...</p>
      </div>
    )
  }

  return (
    <div className="page settings">
      <h1>Settings</h1>
      <p>Configure your campervan system parameters.</p>

      {message && (
        <div className={`message message--${message.type}`}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit} className="settings-form">
        {/* Water Tank Settings */}
        <div className="settings-section">
          <h2>Water Tanks</h2>

          <div className="form-group">
            <label htmlFor="fresh_water_tank_capacity">Fresh Water Tank Capacity (liters)</label>
            <input
              type="number"
              id="fresh_water_tank_capacity"
              value={formData.fresh_water_tank_capacity}
              onChange={(e) => handleInputChange('fresh_water_tank_capacity', e.target.value)}
              min="1"
              step="1"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="grey_water_tank_capacity">Grey Water Tank Capacity (liters)</label>
            <input
              type="number"
              id="grey_water_tank_capacity"
              value={formData.grey_water_tank_capacity}
              onChange={(e) => handleInputChange('grey_water_tank_capacity', e.target.value)}
              min="1"
              step="1"
              required
            />
          </div>
        </div>

        {/* Water Usage Estimates */}
        <div className="settings-section">
          <h2>Usage Estimates</h2>

          <div className="form-group">
            <label htmlFor="daily_base_water_usage">Daily Base Water Usage (liters)</label>
            <input
              type="number"
              id="daily_base_water_usage"
              value={formData.daily_base_water_usage}
              onChange={(e) => handleInputChange('daily_base_water_usage', e.target.value)}
              min="0"
              step="0.5"
              required
            />
            <span className="form-help">Water used daily for drinking, cooking, washing, etc. (excluding showers)</span>
          </div>

          <div className="form-group">
            <label htmlFor="avg_shower_usage">Average Shower Usage (liters)</label>
            <input
              type="number"
              id="avg_shower_usage"
              value={formData.avg_shower_usage}
              onChange={(e) => handleInputChange('avg_shower_usage', e.target.value)}
              min="0"
              step="0.5"
              required
            />
            <span className="form-help">Water used per shower</span>
          </div>
        </div>

        {/* Currency Settings */}
        <div className="settings-section">
          <h2>Currency</h2>

          <div className="form-group">
            <label htmlFor="native_currency">Native Currency</label>
            <CurrencySelect
              id="native_currency"
              value={formData.native_currency}
              onChange={(value) => handleInputChange('native_currency', value)}
              showNames
            />
            <span className="form-help">Currency for expense tracking and conversions</span>
            <span className="form-help form-help--warning">
              Note: Changing currency will not recalculate historical expenses.
              Previously recorded amounts were converted at time of entry and will display incorrect values.
            </span>
          </div>
        </div>

        {/* Battery Settings */}
        <div className="settings-section">
          <h2>Battery</h2>

          <div className="form-group">
            <label htmlFor="battery_charge_avg_window_minutes">Charge Time Estimate Averaging (minutes)</label>
            <input
              type="number"
              id="battery_charge_avg_window_minutes"
              value={formData.battery_charge_avg_window_minutes}
              onChange={(e) => handleInputChange('battery_charge_avg_window_minutes', e.target.value)}
              min="1"
              max="600"
              step="1"
              required
            />
            <span className="form-help">Smoothing window for charge time estimates when charging (1-600 min)</span>
          </div>

          <div className="form-group">
            <label htmlFor="battery_db_write_interval_minutes">Database Write Interval (minutes)</label>
            <input
              type="number"
              id="battery_db_write_interval_minutes"
              value={formData.battery_db_write_interval_minutes}
              onChange={(e) => handleInputChange('battery_db_write_interval_minutes', e.target.value)}
              min="1"
              max="30"
              step="1"
              required
            />
            <span className="form-help">How often to save battery data for statistics (1-30 min)</span>
          </div>

          <div className="form-group form-group--checkbox">
            <label>
              <input
                type="checkbox"
                checked={formData.show_idle_chargers}
                onChange={(e) => setFormData(prev => ({ ...prev, show_idle_chargers: e.target.checked }))}
              />
              Show idle chargers on dashboard
            </label>
            <span className="form-help">Display DC-DC, Solar, and AC-DC sources even when output is 0W</span>
          </div>
        </div>

        {/* Save Button */}
        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </form>

      {/* API Key Section - only show if user has access */}
      {apiKeyLoaded && (
        <div className="settings-section api-key-section">
          <h2>API Key</h2>
          <p className="text-secondary">Use this key to authenticate device endpoints (ESP32, phone sensors). Send it in the X-API-Key header.</p>

          {justSavedKey ? (
            <>
              <div className="api-key-display">
                <div className="api-key-value">
                  <code>{justSavedKey}</code>
                </div>
                <div className="api-key-actions">
                  <button type="button" className="btn" onClick={() => copyApiKey(justSavedKey)}>Copy</button>
                  <button type="button" className="btn" onClick={() => setJustSavedKey(null)}>Done</button>
                </div>
              </div>
              <span className="form-help">Copy this key now and configure your devices. It cannot be shown again after you leave this page.</span>
            </>
          ) : (
            <>
              <div className="api-key-display">
                <div className="api-key-value">
                  <code>{apiKeyConfigured ? '•'.repeat(32) : 'No API key set'}</code>
                </div>
              </div>
              <span className="form-help">
                {apiKeyConfigured
                  ? 'An API key is set. It is stored as a hash and cannot be shown again.'
                  : 'No API key is set. Generate one and copy it before you leave this page.'}
              </span>
            </>
          )}

          {showNewKeyInput ? (
            <div className="api-key-new" style={{ marginTop: '0.75rem' }}>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <input
                  type="text"
                  value={newApiKey}
                  onChange={e => setNewApiKey(e.target.value)}
                  placeholder="Enter new API key (min 16 chars)"
                  className="form-input"
                  style={{ flex: 1 }}
                />
                <button type="button" className="btn" onClick={generateRandomKey}>Generate</button>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                <button type="button" className="btn btn-primary" onClick={saveNewApiKey}>Save</button>
                <button type="button" className="btn" onClick={() => { setShowNewKeyInput(false); setNewApiKey('') }}>Cancel</button>
              </div>
            </div>
          ) : apiKeyConfigured ? (
            <button
              type="button"
              className="btn"
              style={{ marginTop: '0.75rem' }}
              onClick={() => setShowNewKeyInput(true)}
            >
              Set New Key
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-primary"
              style={{ marginTop: '0.75rem' }}
              onClick={() => {
                generateRandomKey()
                setShowNewKeyInput(true)
              }}
            >
              Generate
            </button>
          )}
        </div>
      )}

      {/* Outgoing Webhook Section - admin only */}
      {isAdmin && (
        <div className="settings-section">
          <h2>Outgoing Webhook</h2>
          <p className="text-secondary">
            POST a JSON event to an external URL when a trip departs (first movement) and/or arrives
            (finalized after the grace period). Use the checkboxes to choose which events are sent.
            Leave the URL empty to disable. "Send Test" always sends, regardless of these toggles.
          </p>

          <div className="form-group form-group--checkbox">
            <label>
              <input
                type="checkbox"
                checked={webhookDepartEnabled}
                onChange={(e) => setWebhookDepartEnabled(e.target.checked)}
              />
              Send on depart (first movement)
            </label>
            <span className="form-help">Notify when a trip starts. Off means no depart webhook is sent.</span>
          </div>

          <div className="form-group form-group--checkbox">
            <label>
              <input
                type="checkbox"
                checked={webhookArriveEnabled}
                onChange={(e) => setWebhookArriveEnabled(e.target.checked)}
              />
              Send on arrive (trip finalized)
            </label>
            <span className="form-help">Notify when a trip is finalized with its statistics.</span>
          </div>

          <div className="form-group">
            <label htmlFor="webhook_url">Webhook URL</label>
            <input
              type="url"
              id="webhook_url"
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              placeholder="https://example.com/hooks/trip"
            />
          </div>

          <div className="form-group">
            <label htmlFor="webhook_header_name">Header name</label>
            <input
              type="text"
              id="webhook_header_name"
              value={webhookHeaderName}
              onChange={(e) => setWebhookHeaderName(e.target.value)}
              placeholder="Authorization"
            />
            <span className="form-help">e.g. Authorization or X-API-Key. Empty sends no extra header.</span>
          </div>

          <div className="form-group">
            <label htmlFor="webhook_header_value">Header value</label>
            <input
              type="password"
              id="webhook_header_value"
              value={webhookHeaderValue}
              onChange={(e) => setWebhookHeaderValue(e.target.value)}
              placeholder={webhookHeaderValueMasked || 'Stored value is hidden'}
              autoComplete="new-password"
            />
            <span className="form-help">
              Stored on the server and never shown again
              {webhookHeaderValueMasked ? ` (current: ${webhookHeaderValueMasked})` : ''}.
              Leave blank to keep it; typed values replace it.
            </span>
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button type="button" className="btn btn-primary" onClick={saveWebhook} disabled={savingWebhook}>
              {savingWebhook ? 'Saving...' : 'Save Webhook'}
            </button>
            <button
              type="button"
              className="btn"
              onClick={testWebhook}
              disabled={testingWebhook || !webhookUrl.trim()}
            >
              {testingWebhook ? 'Testing...' : 'Send Test'}
            </button>
            {webhookHeaderValueMasked && (
              <button type="button" className="btn btn-danger" onClick={clearWebhookHeaderValue}>
                Clear Header Value
              </button>
            )}
          </div>
        </div>
      )}

      {/* Scoped read-only API keys - admin only */}
      {isAdmin && (
        <div className="settings-section">
          <h2>Read-Only API Keys</h2>
          <p className="text-secondary">
            Scoped keys for read access only: dashboard, display status, history, and trip
            statistics. They cannot post sensor data, change settings, or use backup/restore.
          </p>

          {createdScopedKey ? (
            <>
              <div className="api-key-display">
                <div className="api-key-value">
                  <code>{createdScopedKey}</code>
                </div>
                <div className="api-key-actions">
                  <button type="button" className="btn" onClick={() => copyApiKey(createdScopedKey)}>Copy</button>
                  <button type="button" className="btn" onClick={() => setCreatedScopedKey(null)}>Done</button>
                </div>
              </div>
              <span className="form-help">Copy this key now. It is stored as a hash and cannot be shown again after you leave this page.</span>
            </>
          ) : (
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <input
                type="text"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                placeholder="Key name (e.g. TravelBot)"
                className="form-input"
                style={{ flex: 1 }}
              />
              <button type="button" className="btn btn-primary" onClick={createScopedKey} disabled={creatingScopedKey}>
                {creatingScopedKey ? 'Creating...' : 'Create Key'}
              </button>
            </div>
          )}

          {scopedKeysLoaded && scopedKeys.length > 0 && (
            <div style={{ marginTop: '1rem' }}>
              {scopedKeys.map((key) => (
                <div
                  key={key.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.5rem 0',
                    borderBottom: '1px solid rgba(128, 128, 128, 0.3)',
                  }}
                >
                  <div>
                    <code>{key.prefix}…</code> <strong>{key.name}</strong>
                    <span className="form-help" style={{ display: 'block' }}>
                      {key.revoked_at
                        ? `Revoked ${new Date(key.revoked_at).toLocaleDateString()}`
                        : `Created ${new Date(key.created_at).toLocaleDateString()} · scope: ${key.scope}`}
                    </span>
                  </div>
                  {!key.revoked_at && (
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={() => revokeScopedKey(key.id, key.name)}
                    >
                      Revoke
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Backup/Restore Section */}
      <div className="settings-section backup-section">
        <h2>Backup & Restore</h2>
        <p className="text-secondary">Download a backup of your database and receipts, or restore from a previous backup.</p>

        <div className="backup-actions">
          <div className="backup-download">
            <h3>Download Backup</h3>
            <p>Creates a ZIP file containing your database and all uploaded receipts.</p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleBackupDownload}
            >
              Download Backup
            </button>
          </div>

          <div className="backup-restore">
            <h3>Restore from Backup</h3>
            <p className="text-warning">Warning: Restoring will replace all current data!</p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".zip"
              onChange={handleRestoreFileSelect}
              style={{ display: 'none' }}
              id="restore-file-input"
            />
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => fileInputRef.current?.click()}
            >
              Select Backup File
            </button>
          </div>
        </div>
      </div>

      {/* Restore Confirmation Dialog */}
      {showRestoreDialog && (
        <div className="modal-overlay">
          <div className="modal restore-modal">
            <div className="modal-header">
              <h2>Confirm Restore</h2>
            </div>
            <div className="modal-body">
              <div className="warning-box">
                <strong>WARNING: This action cannot be undone!</strong>
                <p>Restoring from backup will:</p>
                <ul>
                  <li>Delete ALL current data in the database</li>
                  <li>Replace all receipts with those from the backup</li>
                  <li>Log out all users</li>
                </ul>
              </div>

              <div className="restore-file-info">
                <strong>Selected file:</strong> {restoreFile?.name}
              </div>

              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={restoreUnderstood}
                    onChange={(e) => setRestoreUnderstood(e.target.checked)}
                  />
                  {' '}I understand this will permanently delete all current data
                </label>
              </div>

              <div className="form-group">
                <label htmlFor="restore-confirm">Type RESTORE to confirm:</label>
                <input
                  type="text"
                  id="restore-confirm"
                  value={restoreConfirmText}
                  onChange={(e) => setRestoreConfirmText(e.target.value)}
                  placeholder="RESTORE"
                  autoComplete="off"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn"
                onClick={handleRestoreCancel}
                disabled={restoring}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleRestoreConfirm}
                disabled={restoring || restoreConfirmText !== 'RESTORE' || !restoreUnderstood}
              >
                {restoring ? 'Restoring...' : 'Restore Backup'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
